import { ChangeDetectionStrategy, ChangeDetectorRef, Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { FilterTypes } from 'src/app/modules/shared/enums/filterTypes';
import { Router } from '@angular/router';
import { Observable, tap, throwError } from 'rxjs';
import { FilterViewModel } from 'src/app/modules/shared/models/filter/filterViewModel';
import { FormArray, UntypedFormBuilder, Validators } from '@angular/forms';
import { AddModalKeyboardShortcuts } from 'src/app/modules/shared/enums/addModalKeyboardShortcuts.enum';
import { AllowedFiltersService } from 'src/app/modules/shared/services/allowed-filters.service';
import { AuthenticationService } from 'src/app/modules/shared/services/authentication.service';
import { RiskDetailsViewModel } from '../../../../risk/models/RiskDetailsViewModel';
import { AddModalButtonOptions } from 'src/app/modules/shared/enums/addModalButtonOptions.enum';
import { RisksService } from '../../../../risk/services/risks.service';
import { ValidatedViewModel } from 'src/app/modules/shared/viewModels/validatedViewModel';
import { ConfirmationService } from 'src/app/modules/shared/services/confirmation.service';
import { IconUtilities } from 'src/app/modules/shared/utilities/icon.utilities';
import { IconTypes } from 'src/app/modules/shared/types/iconTypes';
import { ObjectTypes } from 'src/app/modules/shared/enums/objectTypes';
import { EmployeeRoleTypes } from 'src/app/modules/shared/enums/employees/EmployeeRoleTypes';
import { EmployeeUtil } from 'src/app/modules/shared/utilities/employee.utilities';
import { LocalisationService } from 'src/app/modules/shared/services/localisation.service';
import { WtValidators } from 'src/app/modules/shared/reactiveValidators/wtValidators';
import { RiskRAGMatrixService } from 'src/app/modules/shared/services/risk-rag-matrix.service';
import { RiskLikelihoods } from 'src/app/modules/shared/enums/risks/riskLikelihoods';
import { AlertService } from 'src/app/modules/shared/services/alert.service';
import { TranslateService } from '@ngx-translate/core';
import { T } from 'src/assets/i18n/translation-keys';
import { RiskTypes } from 'src/app/modules/risk/enums/riskTypes';
import { RiskStatusTypes } from 'src/app/modules/risk/enums/riskStatusTypes';
import { RiskStrategyTypes } from 'src/app/modules/risk/enums/riskStrategyTypes';
import { ButtonSizes } from '../../../../shared/enums/styles/buttonSizes.enum';
import { ButtonThemes } from '../../../../shared/enums/styles/buttonThemes.enum';
import { catchError } from 'rxjs/operators';
import { RiskImpact } from '../../../../risk/enums/riskImpact';
import { AccountSettingTypes } from 'src/app/modules/shared/enums/accountSettingTypes';
import { AccountSettingsService } from 'src/app/modules/shared/services/account-settings.service';
import { RiskPrivacyStatusTypes } from 'src/app/modules/risk/enums/riskPrivacyStatusTypes';
import { RiskAddModalCoreComponent } from '../risk-add-modal-core';
import { AssignMeAsOwnerOrApproverFilterUtilities } from 'src/app/modules/shared/utilities/shared-owner-filter.utilities';

@Component({
    selector: 'app-risk-add-modal',
    templateUrl: './risk-add-modal.component.html',
    styleUrls: ['./risk-add-modal.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class RiskAddModalComponent extends RiskAddModalCoreComponent implements OnInit, OnDestroy {
  shouldRedirect: boolean = true;

  public risk: RiskDetailsViewModel = new RiskDetailsViewModel();
  public actionGroupFilters: FilterViewModel[][] = [];
  public isHiddenOptionVisible = false;
  public selectedRiskType: RiskTypes = RiskTypes.Risk;
  public dateReseter = false;
  public addModalButtonOptions = AddModalButtonOptions;
  public buttonOptions = AddModalButtonOptions.getOptions(this.translateService);
  public keyboardShortcuts = AddModalKeyboardShortcuts.getKeyShortsAsObj(this.translateService);
  public selectedRisk: FilterViewModel;;

  public excludeDropdownValuesArr = [];
  public exludeRiskStrategyArr = [];
  public readonly riskTypes = RiskTypes;
  public validationErrors: string[];
  public readonly riskPrivacyStatuses = RiskPrivacyStatusTypes;

  public initialSelectedProject: FilterViewModel;
  public initialSelectedType;
  public hasRiskManagerPermission = false;
  public exludeRiskStatuses: number[] = [];
  svg = IconUtilities.getSvgForIconType(IconTypes.Risk);
  public localisedLikelihood: string;

  public today: Date;
  public currentStep: number = 1;
  public isAction: boolean = true;
  public useRiskSearch = false;
  public selectedRiskTypeTitle: string;
  private invalidDescription: boolean = false;

  protected readonly String = String;

  public listToggleOptions = [
    { value: RiskTypes.Risk, displayText: this.localiseRiskType(RiskTypes.Risk), tabId: 'risks' },
    { value: RiskTypes.Issue, displayText: this.localiseRiskType(RiskTypes.Issue), tabId: 'issues' },
    { value: RiskTypes.Opportunity, displayText: this.localiseRiskType(RiskTypes.Opportunity), tabId: 'opportunities' },
  ];

  public selectedListOption = { value: RiskTypes.Risk, displayText: this.localiseRiskType(RiskTypes.Risk), tabId: 'risks' };
  public initedTabs: { [key: string]: boolean } = { risks: true };

  @HostListener('window:resize', ['$event']) onWindowResize(event) {
    this.isMobile = window.innerWidth < this.mobileMaxWidth;
    this.changeDetectorRef.detectChanges();
  }

  constructor(
    protected bsModalRef: BsModalRef,
    protected router: Router,
    protected fb: UntypedFormBuilder,
    protected changeDetectorRef: ChangeDetectorRef,
    protected confirmationService: ConfirmationService,
    protected allowedFiltersService: AllowedFiltersService,
    protected authenticationService: AuthenticationService,
    protected riskService: RisksService,
    protected readonly localisationService: LocalisationService,
    protected readonly riskRAGMatrixService: RiskRAGMatrixService,
    protected readonly alertService: AlertService,
    protected readonly translateService: TranslateService,
    protected readonly wtValidators: WtValidators,
    protected readonly accountSettingsService: AccountSettingsService
  ) {
    super(
      bsModalRef,
      router,
      fb,
      changeDetectorRef,
      confirmationService,
      allowedFiltersService,
      authenticationService,
      riskService,
      localisationService,
      riskRAGMatrixService,
      alertService,
      translateService,
      wtValidators,
      accountSettingsService
    )
  }

  ngOnInit(): void {
    super.ngOnInit();

    this.today = new Date();
    this.selectedRiskTypeTitle = this.localisationService.localise(RiskTypes[this.selectedRiskType]);

    this.hasRiskManagerPermission =
      EmployeeUtil.hasRole(this.employee, EmployeeRoleTypes.Risk_Manager) || EmployeeUtil.IsAdmin(this.employee);
    if (!this.hasRiskManagerPermission) {
      this.exludeRiskStatuses = [RiskStatusTypes.Open, RiskStatusTypes.Closed];
    }

    this.subscriptions.add(
      this.accountSettingsService.getAccountSettingByType(AccountSettingTypes.Enable_Risk_Search).subscribe((res) => {
        this.useRiskSearch = res.value === "1";
      })
    )

    if (!this.initialSelectedType) {
      this.selectType(this.riskTypes.Risk);
    } else {
      this.selectType(this.initialSelectedType);
    }

    this.localisedLikelihood = this.localisationService.localiseFilterType(FilterTypes.Risk_Likelihood);

  }

  initNewForm() {
    this.form = this.fb.group({
      riskTitle: [
        '',
        {
          validators: [
            Validators.required,
            this.wtValidators.textInput(3, 250, WtValidators.RESTRICTED_CHARS.DEFAULT)
          ]
        }
      ],
      riskDescription: ['', Validators.maxLength(2000)],
      owner: [null],
    });
  }

  get actionGroups(): FormArray {
    return this.form.get('actionGroups') as FormArray;
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
  }

  selectType(type: RiskTypes) {
    this.exludeRiskStrategyArr = [];

    switch (type) {
      case RiskTypes.Risk:
      {
        this.selectedRiskType = RiskTypes.Risk;
        this.likelihoodValue = RiskLikelihoods.Highly_Unlikely;
        this.setLikelihoodFilter(RiskLikelihoods.Highly_Unlikely);
        this.setImpactFilter(RiskImpact.Negligible);
        this.excludeDropdownValuesArr = [RiskLikelihoods.Issue];
        this.changeDetectorRef.detectChanges();
        this.riskStrategyValue = RiskStrategyTypes.Mitigate;
        this.svg = IconUtilities.getSvgForIconType(IconTypes.Risk, 24, true);
      }
        break;
      case RiskTypes.Issue: {
        this.selectedRiskType = RiskTypes.Issue;
        this.likelihoodValue = RiskLikelihoods.Issue;
        this.setLikelihoodFilter(RiskLikelihoods.Issue);
        this.setImpactFilter(RiskImpact.Negligible);
        this.excludeDropdownValuesArr = RiskLikelihoods.numValues().filter((v) => v !== RiskLikelihoods.Issue);
        this.changeDetectorRef.detectChanges();
        this.riskStrategyValue = RiskStrategyTypes.Contingency;
        this.svg = IconUtilities.getSvgForIconType(IconTypes.Issue);
        break;
      }
      case RiskTypes.Opportunity:
      {
        this.selectedRiskType = RiskTypes.Opportunity;
        this.likelihoodValue = RiskLikelihoods.Highly_Unlikely;
        this.setLikelihoodFilter(RiskLikelihoods.Highly_Unlikely);
        this.setImpactFilter(RiskImpact.Negligible);
        this.excludeDropdownValuesArr = [RiskLikelihoods.Issue];
        this.changeDetectorRef.detectChanges();
        this.riskStrategyValue = RiskStrategyTypes.Exploit;
        this.svg = IconUtilities.getSvgForIconType(IconTypes.Opportunity);
      }
        break;
      default:
        break;
    }

    this.setRAGInformation();
  }


  get getDueDate() {
    if (this.risk.dueDate) {
      return this.risk.dueDate;
    }
    return '';
  }

  setDueDate(dateAsIsoString: string) {
    this.risk.dueDate = dateAsIsoString;
    this.isTouched = true;
  }

  setAccountForCurrentOwner() {
    const result = AssignMeAsOwnerOrApproverFilterUtilities.addCurrentUserAsOwnerOrApprover(
      this.filters,
      this.employee,
      this.allowedFilters
    );

    this.filters = result.filters;
    this.ownerValue = result.ownerValue;
    this.changeDetectorRef.detectChanges();
  }

  setLikelihoodFilter(likelihood: RiskLikelihoods) {
    const filters = this.filters.filter((f) => f.filterType != FilterTypes.Risk_Likelihood);
    const newLikelihoodFilter = this.allowedFiltersService
      .getCachedAllowedFiltersByType(FilterTypes.Risk_Likelihood)
      .find((f) => f.filterValue == likelihood);
    this.filters.filter((x) => x.filterType == FilterTypes.Risk_Likelihood);
    this.filters = [...filters, newLikelihoodFilter];
  }

  setImpactFilter(impact: RiskImpact) {
    const filters = this.filters.filter((f) => f.filterType != FilterTypes.Risk_Impact);
    const newImpactFilter = this.allowedFiltersService
      .getCachedAllowedFiltersByType(FilterTypes.Risk_Impact)
      .find((f) => f.filterValue == impact);
    this.filters.filter((x) => x.filterType == FilterTypes.Risk_Impact);
    this.filters = [...filters, newImpactFilter];
  }

  public setMyDepartment(): void {
    if (this.allowedFilters) {
      const empFilter = this.allowedFilters.find(
        (f) => f.filterType === FilterTypes.Department && f.filterValue.toString() === this.employee.departmentId.toString()
      );

      if (empFilter && this.filters.indexOf(empFilter) < 0) {
        this.filters = this.filters.slice();
        this.filters.push(empFilter);
      }

    }
    this.changeDetectorRef.detectChanges();
  }

  handleAction(action: string) {
    this.isLoading = true;

    this.subscriptions.add(
      this.handleSubmit().subscribe({
        next: (actionResult: ValidatedViewModel) => {
          const actionErrors = actionResult?.errorList || [];
          if (actionErrors.length > 0) {
            this.validationErrors = actionErrors;
            this.isLoading = false;
            this.changeDetectorRef.markForCheck();
          }
          else {
            this.isLoading = false;
            void this.alertService.success(
              this.translateService.instant(T.common.item_was_added_successfully, { item: this.localiseRiskType(this.selectedRiskType) })
            );
            if (this.shouldRedirect) {
              const contextRoute = RiskTypes[this.selectedRiskType].toLowerCase();
              void this.router.navigate([`v2/risk/risks-issues/${contextRoute}/${this.selectedRisk?.id}`]);
            }
            this.changeDetectorRef.detectChanges();
            this.closeModal();
          }
        },
        error: (err) => {
          this.isLoading = false;
          this.changeDetectorRef.markForCheck();
          return throwError(err);
        }
      })
    )
  }

  getFilterType(field: string): FilterTypes {
    switch (field) {
      case 'status':
        return FilterTypes.Risk_Action_Status;
      case 'type':
        return FilterTypes.Risk_Action_Type;
      case 'filters':
        return FilterTypes.Owner;
      case 'userGroup':
        return FilterTypes.User_Group;
      default:
        throw new Error(`Unknown field: ${field}`);
    }
  }


  handleSubmit(): Observable<any> {
    this.risk.filters = this.filters;
    this.risk.title = this.form.controls.riskTitle.value.trim();
    this.risk.description = this.form.controls.riskDescription.value?.trim();
    this.risk.type = this.selectedRiskType;
    this.risk.privacyStatus = this.getFilterValue(FilterTypes.Risk_Privacy_Status);
    this.risk.impact = this.getFilterValue(FilterTypes.Risk_Impact);
    this.risk.likelihood = this.getFilterValue(FilterTypes.Risk_Likelihood);
    this.risk.riskStrategyId = this.getFilterValue(FilterTypes.Risk_Strategy);
    this.risk.ownerId = this.getFilterValue(FilterTypes.Owner);
    this.risk.createdById = this.employee.id;
    this.risk.identifiedById = this.getFilterValue(FilterTypes.Identified_By);
    this.risk.status = this.getFilterValue(FilterTypes.Risk_Status);


    return this.riskService.add(this.risk).pipe(
      tap((riskResult: ValidatedViewModel) => {
        this.selectedRisk = riskResult.returnModel;
      }),
      catchError(error => {
        return throwError(error);
      })
    );
  }

  initAllowedFilters() {
    this.allowedFilters = this.allowedFiltersService.getCachedAllAllowedFilters();
    this.initInitalSelectedFields();
  }

  initInitalSelectedFields() {
    if (this.initialSelectedProject) {
      const filteredprj = this.allowedFilters.find(
        (f) =>
          f.filterType == FilterTypes.Project && f.filterValue.toString() == this.initialSelectedProject.filterValue.toString()
      );
      this.filters.push(filteredprj);
    }

    this.filters = this.filters.slice();
    this.changeDetectorRef.markForCheck();
  }

  localiseString(s: string) {
    return this.localisationService.localise(s);
  }

  validateState() {
    return this.form.get('riskTitle')?.valid
    && this.statusValue !== 0
    && this.impactValue !== 0
    && this.likelihoodValue !== 0 as RiskLikelihoods
    && this.privacyValue
    && this.ownerValue
    && !this.invalidDescription;
  }


  onSave(event: string) {
    this.handleAction(event);

    document.dispatchEvent(new KeyboardEvent('keydown', { key: 'escape' }));
  }

  public localiseRiskType(riskType: RiskTypes): string {
    switch (riskType) {
      case RiskTypes.Risk:
        return this.localisationService.localiseObjectType(ObjectTypes.Risk);
      case RiskTypes.Issue:
        return this.translateService.instant(T.defaultLocalizations.issue.one);
      case RiskTypes.Opportunity:
        return this.translateService.instant(T.defaultLocalizations.opportunity.one);
      default:
        return 'unknown';
    }
  }

  onTabSelect(id: string) {
    this.initedTabs[id] = true;
    this.selectedRiskTypeTitle = this.localisationService.localise(RiskTypes[this.selectedRiskType]);
    this.ragDescription = this.localisationService.localiseFilterValueByFilterType(this.risk.rag, this.filterTypes.Risk_RAG);
    this.changeDetectorRef.detectChanges();
  }

  public toggleListChange(currentState: { value: RiskTypes; displayText: string, tabId: string }): void {
    this.selectedListOption = currentState;
    this.selectType(currentState.value);

    this.onTabSelect(currentState.tabId);
  }

  public isDescriptionInvalid(invalid: boolean) {
    this.invalidDescription = invalid;
    this.changeDetectorRef.detectChanges();
  }
}
